import { useEffect, useState, useMemo, useRef } from "react"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { useLocation } from "react-router"
import "./index.scss"

import { ConfigContainer, Loading } from "components"
import { Title, Text, Container, DurationInput, Select, TaskStatePicker } from "components/Form"
import { message, samples, analysisDefinitions, limsResults, limsConsumables, limsForskrifter, limsSstSpec, limsSstResult, limsSpecs } from "state_management"
import {
  selectActiveConsumables,
  validityStatus,
  consumableType,
  CONSUMABLE_TYPE_FIELDS,
  standardStatus,
  isStandardExpiringSoon,
  standardClassLabelKey,
} from "state_management/lims/limsConsumables"

import { DateTime, Duration } from "luxon"

import AssetSetup from "./AssetSetup.jsx"
import PeopleSetup from "./PeopleSetup.jsx"
import ResultEntryPanel from "components/Lims/ResultEntryPanel"
import SstPanel from "components/Lims/SstPanel"
import ForskriftViewer from "components/Lims/ForskriftViewer"
import ESignatureModal from "components/Lims/ESignatureModal"

import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { FiPlus, FiAlertTriangle, FiCamera } from "react-icons/fi"
import { Button } from "react-bootstrap"
import useT from "utils/hooks/useT"
import { isTaskBeyondMoveGrace } from "utils/scripts/lockGrace"

// Config used for analysis tasks that are on the plan, and analysis task "defintion" setup.
export default function AnalysisTaskConfig({
  task,
  title,
  isDefinition = false,
  isDraft = false,
  submitHandler,
  cancelHandler,
  deleteHandler,
  bookedAndFreeAssets,
  activeAssetId,
  disabled = false,
  // SPEC-060 (#4132): which part of this form to render, so the analysis popup can offer result
  // entry as its own TAB without splitting the form into two components. Both tabs render the SAME
  // instance with the same `key`, so the state stays in one place and a tab switch neither
  // remounts nor loses unsaved choices. "all" keeps every existing call site unchanged.
  sections = "all",
}) {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const competenceGroups = useSelector((state) => state.competenceGroups)
  const assets = useSelector((state) => state.asset.assets)
  const analysis = useSelector((state) => state.analyses.plannedAnalyses[task.analysisId])
  const members = useSelector((state) => state.members)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)

  // LIMS QC result entry (F-C2) — a collapsible panel on a PLANNED analysis task, shown only
  // when the LIMS module is on. The analysis definition is resolved by NAME (planned analyses
  // carry no stable definition id); when no definition / spec matches, the panel degrades to a
  // "no specification" message. The result-entry surface is irrelevant to a definition-config
  // view, so it is gated on !isDefinition too. With LIMS off this is entirely inert.
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const analysisDefinitionId = useSelector((state) =>
    showLims && !isDefinition && task.analysisId
      ? limsResults.selectAnalysisDefinitionIdForAnalysis(state, task.analysisId)
      : null
  )
  // F8 (SPEC-040): the high-level consumable requirements for this analysis (type / quantity /
  // standardClass), read off the folded spec. Drives the picker's hard filtering + the reason-
  // giving rejection of a manually-entered/scanned id that does not qualify. Empty when no spec /
  // no requirements (then the picker behaves exactly as before — all active consumables offered).
  const requiredConsumables = useSelector((state) => {
    const spec = analysisDefinitionId ? limsSpecs.selectSpec(state, analysisDefinitionId) : undefined
    return (spec && spec.requiredConsumables) || []
  }, shallowEqual)
  // The effective forskrift (controlled method document) for this analysis definition, surfaced
  // read-only next to result entry (PRD-003 Area 2 / SPEC-008). Resolved by the same
  // analysisDefinitionId; null when no linked-and-effective forskrift (soft gate — the analysis
  // still runs).
  const effectiveForskrift = useSelector(
    (state) =>
      showLims && !isDefinition && analysisDefinitionId
        ? limsForskrifter.selectEffectiveForDefinition(state, analysisDefinitionId)
        : null,
    shallowEqual
  )
  // Whether any forskrift document is linked to this definition at all — drives the soft warning
  // when a method is linked but has no effective version yet (linked + not-effective). When
  // nothing is linked we show no forskrift block (no noise on non-LIMS-governed analyses).
  const hasLinkedForskrift = useSelector((state) =>
    showLims && !isDefinition && analysisDefinitionId
      ? limsForskrifter
          .selectDocuments(state)
          .some((d) => (d.analysisDefinitionIds || []).includes(analysisDefinitionId))
      : false
  )
  // Whether this analysis definition REQUIRES a System Suitability Test (PRD-005 Area 3 / SPEC-017).
  // When it does, the SST panel mounts above result entry and the send-to-review hard gate applies
  // (enforced in ResultEntryPanel). Inert when LIMS is off / no definition resolved.
  const requiresSst = useSelector((state) =>
    showLims && !isDefinition && analysisDefinitionId
      ? limsSstSpec.selectRequiresSst(state, analysisDefinitionId)
      : false
  )
  // #4155 removed the SST-ready badge that used to sit here, and the `sstReady` selector with it.
  // The SST verdict is still read where it is acted on: the SST panel below, and the send-to-review
  // hard gate in ResultEntryPanel (which owns its own `isSstPassedAndSigned` read).
  // Whether the "QC Results & Approvals" accordion is open, in "all" mode where it IS an accordion.
  // SPEC-060 (#4132) retired the `focusQcSection` prop that used to seed this: a LIMS deep-link now
  // opens the popup's Result-entry TAB instead, and in that tab the section is always open
  // (`qcAlwaysOpen`), so there was nothing left for the prop to influence. History worth keeping:
  // SPEC-022 K2 introduced it to expand the section for review/correction tiles, Reviews cards and
  // the QA worklist; RR3 #9 then dropped its auto-scroll because jumping the modal read as
  // disorienting. Both concerns are now served better by the tab.
  const [showResults, setShowResults] = useState(false)

  // LIMS consumables used for this QC run (F-C2, PRD-003 Area 3). The selection lives on the
  // analysis task, under Additional Information (like the Primary Instrument); it is passed to
  // the result panel and snapshotted onto each result (the snapshot field is still named
  // reagentLotIds for back-compat). One combined picker offers active consumables of every type
  // (reagent / standard / column / glassware). Loaded/shown only when LIMS is on for a planned
  // analysis. existingResultsForAnalysis is shallow-compared so it does not change identity on
  // every render (selectResultsForAnalysis returns a fresh array), which would otherwise re-run
  // the seed below.
  const activeConsumables = useSelector(selectActiveConsumables)
  const allConsumables = useSelector(limsConsumables.selectConsumables)
  const existingResultsForAnalysis = useSelector(
    (state) =>
      showLims && !isDefinition && task.analysisId
        ? limsResults.selectResultsForAnalysis(state, task.analysisId)
        : [],
    shallowEqual
  )
  const [reagentLotIds, setReagentLotIds] = useState([])
  const reagentSeeded = useRef(false)

  // Start with setuptime missing, and default of 0 for other fields
  const [name, setName] = useState(task.name || "")
  const [teamMember, setTeamMember] = useState(task.memberIds ? task.memberIds[0] : [])
  const [state, setState] = useState(task.state || "NOT_STARTED")
  const [description, setDescription] = useState(task.description ? task.description.trim() : "")
  const [startDate, setStartDate] = useState(task.start ? DateTime.fromISO(task.start).setZone(teamTimeZone) : null)
  const [startTime, setStartTime] = useState(
    startDate ? Duration.fromObject({ hours: startDate.hour, minutes: startDate.minute }) : null
  )
  const [setuptime, setSetuptime] = useState(
    task.setuptime !== undefined ? Duration.fromObject({ seconds: task.setuptime }) : null
  )
  const [perSampleDuration, setPerSampleDuration] = useState(
    Duration.fromObject({ seconds: task.perSampleDuration || 0 })
  )
  const [allowedGroupIds, setAllowedGroupIds] = useState(
    task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : []
  )
  const [showAdditionals, setShowAdditionals] = useState(false)
  const [assetRequirements, setAssetRequirements] = useState(
    task.assetRequirements ?
    task.assetRequirements.map(req => ({
      assetSetuptime: Duration.fromObject({ seconds: req.assetSetuptime || 0 }),
      assetPerSampleDuration: Duration.fromObject({ seconds: req.assetPerSampleDuration || 0 }),
      assetOffset: Duration.fromObject({ seconds: req.assetOffset || 0 }),
      assetId: req.assetId || null,
      allowedAssetIds: req.allowedAssetIds ? req.allowedAssetIds : []
    }))
    :
    []
  );
  const activeAssetRequirement = assetRequirements.find(req => req.assetId === activeAssetId);
  const [startTimeAssetView, setStartTimeAssetView] = useState(
    startDate && activeAssetRequirement?.assetOffset ? Duration.fromObject({ hours: startDate.hour, minutes: startDate.minute }).plus(activeAssetRequirement?.assetOffset) : null
  )
  const assetIds = useMemo(
    () => assetRequirements.map(req => req.assetId).filter(id => id != null),
    [assetRequirements]
  );
  const primaryAsset = assetIds[0] || null;

  // Consumables offered (F8 / SPEC-040): active (valid) ones of every type, HARD-FILTERED to those
  // that satisfy a consumable requirement when the analysis declares any (only qualifying + active
  // items are selectable — the USER decision in 9a). With NO requirements the picker is unchanged
  // (every active consumable offered). Any already-selected item is kept visible even if it has
  // since expired/become overdue or no longer qualifies (so the analyst still sees what they
  // chose — a historical fallback, never silently dropped). Labelled with its type,
  // e.g. "HPLC C18 — SN-4471 (Column)".
  const reagentCollection = useMemo(() => {
    const byId = {}
    activeConsumables.forEach((c) => {
      if (matchesRequirements(c, requiredConsumables)) byId[c.id] = c
    })
    reagentLotIds.forEach((id) => {
      if (!byId[id]) {
        const found = allConsumables.find((c) => c.id === id)
        if (found) byId[id] = found
      }
    })
    return Object.values(byId)
      .map((c) => ({ id: c.id, name: consumableLabel(c, t) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [activeConsumables, allConsumables, reagentLotIds, requiredConsumables, t])
  // The selected consumables paired with a VISIBLE per-item status badge, so a used-up column,
  // overdue flask or — for a reference standard — an approved/quarantine/expiring-soon/expired/
  // discarded state is obvious, not only surfaced as a soft warning (PRD-003 Area 3 / SPEC-015).
  // For a standard the richer standardStatus() drives the badge; other types keep validityStatus.
  const selectedConsumables = reagentLotIds
    .map((id) => allConsumables.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => ({ consumable: c, badge: consumableBadge(c) }))
  const invalidSelectedConsumables = selectedConsumables.filter((s) => s.badge.invalid)

  // F8 (SPEC-040): per-requirement coverage — whether at least one CURRENTLY-selected consumable
  // satisfies each declared requirement. Drives the fail-fast completeness hint near the picker
  // (the server is the authoritative gate at send-to-review). The selected pool is the live
  // consumable records for the chosen ids.
  const selectedConsumableRecords = useMemo(
    () => reagentLotIds.map((id) => allConsumables.find((c) => c.id === id)).filter(Boolean),
    [reagentLotIds, allConsumables]
  )
  const requirementCoverage = useMemo(
    () =>
      requiredConsumables.map((req) => ({
        requirement: req,
        covered: selectedConsumableRecords.some((c) => matchesRequirement(c, req)),
      })),
    [requiredConsumables, selectedConsumableRecords]
  )
  const uncoveredRequirements = requirementCoverage.filter((r) => !r.covered)

  // Consumable ids already SNAPSHOTTED on a saved (in-review / sent-to-supervisor / sent-to-qa)
  // result for this analysis. Changing/removing one of these is a GMP-relevant change of a recorded
  // fact (PRD-003 Area 3): it requires a stated reason + audit. A draft-stage selection change is
  // free (no reason). SPEC-011 renamed the post-review state to `sent-to-qa` (isReviewSignedOff
  // covers the legacy `reviewed` too); SPEC-035 (F3) adds the sent-to-supervisor review phase.
  const lockedConsumableIds = useMemo(() => {
    const ids = new Set()
    existingResultsForAnalysis.forEach((r) => {
      const inReviewPipeline =
        r.state === "in-review" || r.state === "sent-to-supervisor" || limsResults.isReviewSignedOff(r)
      if (inReviewPipeline && Array.isArray(r.reagentLotIds)) {
        r.reagentLotIds.forEach((id) => ids.add(id))
      }
    })
    return ids
  }, [existingResultsForAnalysis])
  // A pending selection change awaiting a reason: { next, removed: [{id, name}] }. null = none.
  const [pendingSwap, setPendingSwap] = useState(null)

  // Intercept the consumables picker (F8 / SPEC-040):
  //   1. REASON-GIVING REJECTION — any NEWLY-added id that does not qualify (expired / overdue /
  //      not in stock / fails the analysis's consumable requirements) is rejected with an
  //      explained warning and dropped from the applied selection. The dropdown is already hard-
  //      filtered, but a scanned/typed id (or one that expired since it was listed) bypasses that
  //      filter — so the rejection is explained, never silent.
  //   2. LOCKED-SWAP GUARD — removing an id snapshotted on a saved result defers until a reason is
  //      signed (unchanged). Otherwise the (cleaned) selection applies immediately.
  const handleConsumablesChange = (next) => {
    const added = next.filter((id) => !reagentLotIds.includes(id))
    const rejected = []
    added.forEach((id) => {
      const consumable = allConsumables.find((c) => c.id === id)
      const reasonKey = rejectionReason(consumable, requiredConsumables)
      if (reasonKey) {
        rejected.push({ id, name: consumable ? consumableLabel(consumable, t) : id, reasonKey })
      }
    })
    if (rejected.length > 0) {
      rejected.forEach((r) => dispatch(message.warning(t(r.reasonKey, { consumable: r.name }))))
      next = next.filter((id) => !rejected.some((r) => r.id === id))
    }
    const removedLocked = reagentLotIds.filter((id) => !next.includes(id) && lockedConsumableIds.has(id))
    if (removedLocked.length === 0) {
      setReagentLotIds(next)
      return
    }
    const removed = removedLocked.map((id) => {
      const c = allConsumables.find((x) => x.id === id)
      return { id, name: c ? consumableLabel(c, t) : id }
    })
    setPendingSwap({ next, removed })
  }

  // The analyst signed (reason-only) the swap: apply the deferred selection. The stated reason
  // is deliberate friction for changing a recorded fact; the change itself is captured when the
  // re-saved result (with the new reagentLotIds) lands server-side, where the audit entry is
  // written (SPEC-021 B4 — the client no longer posts audit entries).
  const handleSwapSign = () => {
    setReagentLotIds(pendingSwap.next)
    setPendingSwap(null)
  }

  const sortedAllowedPrimaryAssetIds = assetRequirements[0] ? assetRequirements[0].allowedAssetIds
    .map(id => assets[id])
    .filter(asset => asset)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(asset => asset.id)
    :
    []


  const handleAssetRequirementChange = (index, field, value) => {
    const updatedRequirements = assetRequirements.map((requirement, reqIndex) => {
      if (index === reqIndex) {
        return { ...requirement, [field]: value };
      }
      return requirement;
    });
    setAssetRequirements(updatedRequirements);
  };

  const addAssetRequirement = () => {
    const newRequirement = {
      assetSetuptime: Duration.fromObject({ seconds: 0 }),
      assetPerSampleDuration: Duration.fromObject({ seconds: 0 }),
      assetOffset: Duration.fromObject({ seconds: 0 }),
      assetId: null,
      allowedAssetIds: []
    };
    setAssetRequirements([...assetRequirements, newRequirement]);
  };

  const removeAssetRequirement = (index) => {
    setAssetRequirements(currentRequirements =>
      currentRequirements.filter((_, reqIndex) => index !== reqIndex)
    );
  };

  let sortedCollection = [];
  // Check if bookedAndFreeAssets is not null or undefined
  if (bookedAndFreeAssets) {
    let combinedAssets = [];

    // Iterate over each index
    Object.values(bookedAndFreeAssets).forEach(index => {
      const freeAssets = index.freeAssets || [];
      const bookedAssets = index.bookedAssets || [];

      // Combine free and booked assets from the index, with appropriate status
      combinedAssets = combinedAssets.concat(
        freeAssets.map(assetId => ({
          ...assets[assetId],
          status: t("assetStatusFree"),
        })),
        bookedAssets.map(assetId => ({
          ...assets[assetId],
          status: t("assetStatusBooked"),
        }))
      );
    });

    // Sort the combined assets alphabetically by their name and append the status to the name
    sortedCollection = combinedAssets.sort((a, b) => a.name.localeCompare(b.name))
      .map(asset => ({
        ...asset,
        name: `${asset.name} (${asset.status})`
      }));
  }
  const allowedPrimaryAssetIds = sortedAllowedPrimaryAssetIds
  .map((id) => sortedCollection.find((asset) => asset.id === id))
  .filter(Boolean);


  // SPEC-046 (F4): whether the planned analysis has already started — its authoritative
  // earliestTaskStart (backend SPEC-047) lies before today 00:00 in the team timezone (the backend
  // lock boundary). Evaluated per render so the verdict never goes stale across midnight. Drives
  // the reason-aware ghost tooltips below; undefined `analysis` (definition config) stays false.
  const analysisStarted =
    analysis?.earliestTaskStart != null &&
    DateTime.fromISO(analysis.earliestTaskStart) < DateTime.now().setZone(teamTimeZone).startOf("day")

  // SPEC-047 (R17) + SPEC-068 R21 (#4166): on an executed analysis (DONE/FAILED samples) a task's
  // start is frozen — the backend refuses changing it (406) — only once the task was due MORE THAN
  // 3 calendar days ago (isTaskBeyondMoveGrace, team tz, evaluated per render so it never goes
  // stale across midnight). A task due within the last 3 days, or a FUTURE task, stays editable
  // (this replaces the old blanket SPEC-046 F5 block and R17's today-00:00 rule). Echoing the
  // UNCHANGED start on save is fine (the backend only refuses actual changes). Definitions have no
  // planned analysis, so this stays off there.
  const startChangeBlocked =
    !isDefinition && !!analysis?.hasCompletedOrFailedSamples && isTaskBeyondMoveGrace(task.start, teamTimeZone)

  const location = useLocation()
  const isAnalysisSetupView = location.pathname.includes("/analyses/")
  const isAssetView = location.pathname.includes("/asset-view")

  const sampleData = useSelector((state) => state.samples[task.analysisId] || null)

  if (!sampleData && isAssetView) {
    dispatch(samples.fetchForPlanned(task.analysisId))
  }

  // SPEC-048 (R19): the backend now AUTHORITATIVELY refuses DELETE /analysis (406) when a LIMS
  // analysis carries QC results — mirroring the unplan ANALYSIS_HAS_RESULTS guard — so deleting can
  // never orphan an e-signed QcResult. This is a real server backstop, not best-effort: the ghost
  // here is a UX pre-empt of that refusal (the results slice may not be hydrated on the first popup
  // open, in which case the button stays active and the backend returns the 406 the FE surfaces as a
  // toast). The DONE/FAILED causes are covered by hasCompletedOrFailedSamples since SPEC-047 R11.
  // SPEC-044: explicitly gated on showLims — a non-LIMS team must never derive block-state from
  // the LIMS results slice (explicit gate, not an empty-slice assumption).
  const analysisHasResults = useSelector((state) =>
    showLims && !isDefinition && task.analysisId
      ? state.limsResults.items.some((r) => r.analysisId === task.analysisId)
      : false
  )

  // Reason-aware tooltip for the ghosted delete button (mirrors the Add Samples ghost in
  // SampleTableForPlannedAnalysis): executed wins — it can never be lifted (R6) — then started,
  // then LIMS QC results; otherwise an auto-locked past analysis points at moving it forward
  // (K14 makes "unlock it" dead-end advice).
  let deleteDisabledTitleKey = "blockedActionLocked"
  if (analysis?.hasCompletedOrFailedSamples) deleteDisabledTitleKey = "blockedActionDeleteCompleted"
  else if (analysisStarted) deleteDisabledTitleKey = "blockedActionStarted"
  else if (analysisHasResults) deleteDisabledTitleKey = "limsUnplanBlockedQc"

  // Load the previously-saved F-C2 results + reference data so the result panel can render and
  // the name→definition resolution has data. No-op (and no fetch) when LIMS is off. SPEC-021
  // (B2): the QC/review/SST specs ride on the analysis-definition payload, so ONE definitions
  // fetch hydrates all three spec slices; QC capabilities ride on the member dump (B3) and need
  // no fetch of their own.
  useEffect(() => {
    if (showLims && !isDefinition) {
      dispatch(analysisDefinitions.fetch())
      dispatch(limsResults.fetch())
      dispatch(limsConsumables.fetch())
      // The effective forskrift surfaced read-only next to result entry (PRD-003 Area 2).
      dispatch(limsForskrifter.fetch())
      // Any saved per-run SST result (PRD-005 Area 3) — feeds the SST panel + send gate.
      dispatch(limsSstResult.fetch())
    }
  }, [showLims, isDefinition, dispatch])

  // Seed the reagent lots ONCE from the latest saved result for this analysis (resume the run),
  // then never overwrite the analyst's selection — guarded by a ref so later result updates (e.g.
  // after send-to-review) don't clobber an in-progress choice.
  useEffect(() => {
    if (reagentSeeded.current) return
    const seedFrom = existingResultsForAnalysis.find((r) => r.reagentLotIds && r.reagentLotIds.length)
    if (seedFrom) {
      setReagentLotIds(seedFrom.reagentLotIds)
      reagentSeeded.current = true
    }
  }, [existingResultsForAnalysis])

  useEffect(() => {
    // Update the start time view when the startDate or active asset's offset changes
    if (startDate && activeAssetRequirement?.assetOffset) {
      setStartTimeAssetView(Duration.fromObject({ hours: startDate.hour, minutes: startDate.minute }).plus(activeAssetRequirement.assetOffset));
    }
  }, [startDate, activeAssetRequirement]);

  const [endDate, setEndDate] = useState(DateTime.fromISO(task.end).setZone(teamTimeZone));
  const [durationValue, setDurationValue] = useState(null);

  // Used to update the day and time so it doesn't get above 24 hours
  useEffect(() => {
    if (sampleData && !sampleData.isLoading) {
      let taskStartTime = DateTime.fromISO(task.start).setZone(teamTimeZone);
      let taskEndTime = DateTime.fromISO(task.end).setZone(teamTimeZone);

      // When isAssetView is true, adjust the taskStartTime by adding the assetOffset
      if (isAssetView && activeAssetRequirement) {
        const offsetInMinutes = activeAssetRequirement.assetOffset / 60000;
        taskStartTime = taskStartTime.plus({ minutes: offsetInMinutes });
        taskEndTime = taskStartTime;
      }

      let totalMinutes = 0;

      // Convert assetSetuptime and assetPerSampleDuration from milliseconds to minutes
      if (isAssetView && activeAssetRequirement?.assetSetuptime) {
        const setupTimeInMinutes = activeAssetRequirement.assetSetuptime / 60000;
        const perSampleDurationInMinutes = (activeAssetRequirement.assetPerSampleDuration / 60000) * sampleData.items.length;
        totalMinutes = setupTimeInMinutes + perSampleDurationInMinutes;
      } else {
        // Convert setuptime and perSampleDuration from milliseconds to minutes
        if (setuptime) {
          totalMinutes = setuptime / 60000;
        }
        if (perSampleDuration) {
          totalMinutes += (perSampleDuration / 60000) * sampleData.items.length;
        }
      }

      const hoursToAdd = Math.floor(totalMinutes / 60);
      const minutesToAdd = totalMinutes % 60;

      let newEndTime = isAssetView ? taskEndTime.plus({ hours: hoursToAdd, minutes: minutesToAdd }) : taskStartTime.plus({ hours: hoursToAdd, minutes: minutesToAdd });

      // Update endDate when the day has rolled over, or when in asset view
      if (newEndTime.day !== taskEndTime.day || isAssetView) {
        setEndDate(newEndTime);
      }

      setDurationValue(Duration.fromObject({
        hours: newEndTime.hour,
        minutes: newEndTime.minute,
      }));

      if (isAssetView) {
        setStartTimeAssetView(Duration.fromObject({
          hours: taskStartTime.hour,
          minutes: taskStartTime.minute,
        }));
      }
    }
  }, [task.end, task.start, teamTimeZone, isAssetView, activeAssetRequirement, sampleData, setuptime, perSampleDuration]);

  // Make description text-area more dynamic by requiring the same amount of row as the 'description' string
  const descriptionTextRows = description.slice().split(/\r\n|\r|\n/).length || 1

  // SPEC-060 — the two halves this form can render. Deliberately derived (not props) so "all"
  // stays the single default and no existing caller has to opt in.
  const showTaskSections = sections !== "qc"
  const showQcSections = sections !== "task"
  // When the QC section IS the tab, it is always open — the accordion only makes sense while it
  // is one section among the task fields.
  const qcAlwaysOpen = sections === "qc"

  if ((!sampleData || sampleData.isLoading) && isAssetView) {
    return <Loading />
  }

  return (
    <ConfigContainer
      disabled={disabled}
      title={title}
      submitHandler={() => {
        const res = {
          name,
          setuptime: setuptime ? setuptime.as("seconds") : null,
          perSampleDuration: perSampleDuration.as("seconds"),
          allowedGroupIds,
          description,
          // Send ONLY the five fields the backend's task endpoints accept for an
          // assetRequirement (the same whitelist as analysisTasks.getBookedFreeAssets'
          // /task/asset-information PATCH). Spreading `...req` here previously leaked any
          // other property carried on a requirement object (e.g. a per-requirement id from
          // the fetch, or a field merged in by the definition-config page) into the PATCH
          // body, which the analysis-task endpoint rejects with a 422 ("Some data was
          // invalid, please retry"). Building each requirement explicitly keeps the payload
          // strictly to the known contract.
          assetRequirements: assetRequirements.map(req => ({
            assetSetuptime: req.assetSetuptime ? req.assetSetuptime.as('seconds') : 0,
            assetPerSampleDuration: req.assetPerSampleDuration ? req.assetPerSampleDuration.as('seconds') : 0,
            assetOffset: req.assetOffset ? req.assetOffset.as('seconds') : 0,
            assetId: req.assetId ? req.assetId : null,
            allowedAssetIds: req.allowedAssetIds || []
          })),
        }
        if (!isDefinition) {
          if (state !== "NOT_STARTED") {
            const curDate = DateTime.local().setZone(teamTimeZone).startOf("day").toISODate()
            const endDateISO = endDate.startOf("day").toISODate()
            if (endDateISO > curDate) {
              const stateMsg = state === "STOPPED" ? t("finished").toLowerCase() : state === "PAUSED" ? t("paused").toLowerCase() : t("started").toLowerCase()
              const msg = t("futureAnalysisTaskCantBe", { state: stateMsg })
              dispatch(message.warning(msg))
              return
            }
          }
          res.state = state
          res.start = startDate.set({ hour: startTime.hours, minute: startTime.minutes }).toISO()
        }
        res.memberIds = Array.isArray(teamMember) ? teamMember : [teamMember]
        const isValid = res.assetRequirements.every((req, index) => {
          if (index > 0 && (!req.allowedAssetIds || req.allowedAssetIds.length === 0)) {
            dispatch(message.warning(t("secondaryInstrumentMustHaveAllowedInstrument")));
            return false
          }
          return true
        })
        if (isValid) {
          submitHandler(res);
        }

        // Tasks in setup view have analysisDefinitionId while tasks in plan view have analysisId
        // The following dispatch is only relevant for planned analyses
      }}
      cancelHandler={cancelHandler}
      canCancelWhenDisabled={disabled}
      deleteTitle={!isDefinition ? t("unplanAnalysis") : undefined}
      deleteHandler={deleteHandler}
      // SPEC-045 (K3/K4): a hard-locked planned analysis cannot be unplanned/deleted — ghost the
      // delete button (both the schedule and asset-view popups render this component). Definitions
      // have no planned analysis, so `analysis` is undefined and this stays off.
      // SPEC-046 follow-up (adversarial review): the backend refuses delete on FOUR independent
      // signals — samplesLocked (K3), the R2 date guard (started), R6 (DONE/FAILED samples), and
      // the LIMS QC-results guard — so the ghost mirrors all four. Previously only samplesLocked
      // ghosted, and e.g. an unswept analysis whose single task is TODAY with a DONE sample
      // rendered an active button that 406'd.
      // SPEC-046 (F4): reason-aware tooltip via deleteDisabledTitleKey (computed above).
      deleteDisabled={
        !!analysis?.samplesLocked || !!analysis?.hasCompletedOrFailedSamples || analysisStarted || analysisHasResults
      }
      deleteDisabledTitle={t(deleteDisabledTitleKey)}
      submitButtonName={t("save")}
      cancelButtonName={t("cancel")}
    >
      <div className="setup-container">
        {showTaskSections && (
          <>
            <Container>
              <Title>{t("analysis")}</Title>
              <Text disabled value={analysis ? analysis.name : ""} />
            </Container>
            <Container>
              <Title>
                {t("taskName")}<div className="red-text">*</div>
              </Title>
              <Text disabled={disabled} required={!isDraft} value={name} onChange={setName} />
            </Container>
          </>
        )}
        {showTaskSections && !isDefinition && (
          <>
            <Container>
              <Title>
                {t("teamMember")} <div className="red-text">*</div>
              </Title>
              <Select
                name={t("teamMember")}
                required
                closeMenuOnSelect={true}
                attr={"initials"}
                disabled={disabled}
                collection={Object.values(members).filter(
                  (member) => (!member.archived || task.memberIds[0] === member.id) && member.showOnPlan
                )}
                value={teamMember}
                onChange={setTeamMember}
                isMulti={false}
                menuPosition="fixed"
              />
            </Container>
            <Container>
              <Title>{t("primaryInstrument")}</Title>
              <Select
                name={t("primaryInstrument")}
                disabled={disabled}
                collection={allowedPrimaryAssetIds[0] ? allowedPrimaryAssetIds : []}
                value={primaryAsset}
                onChange={(newAssetIds) => {
                  handleAssetRequirementChange(0, 'assetId', newAssetIds[0])
                }}
                menuPlacement="bottom"
                isMulti={false}
              />
            </Container>
            <div className="container-compartment">
              <>
                {isAssetView ? (<p style={{ marginBottom: "1rem" }}>{t("instrumentTime")}</p>) : (<p style={{ marginBottom: "1rem" }}>{t("memberTime")}</p>)}
                {/* SPEC-047 (R17) / SPEC-068 (R21): a task frozen beyond the three-day move grace on
                    an executed analysis has its start ghosted — the wrapper span carries the
                    not-allowed cursor + tooltip (disabled inputs would show neither reliably),
                    display:block so the InputGroup keeps its normal full-width layout. */}
                <span
                  className={startChangeBlocked ? "blocked-action-wrap" : undefined}
                  style={startChangeBlocked ? { display: "block" } : undefined}
                  title={startChangeBlocked ? t("blockedActionTaskBeyondMoveGrace") : undefined}
                >
                  <Container>
                    <Title>{t("fromDate")}</Title>
                    <Text
                      type="date"
                      required
                      disabled={isAssetView || startChangeBlocked ? true : disabled}
                      value={startDate ? startDate.toFormat("yyyy-MM-dd") : ""}
                      onChange={(value) => {
                        if (!value) {
                          setStartDate(undefined)
                          return
                        }
                        try {
                          const newDate = DateTime.fromFormat(value, "yyyy-MM-dd").setZone(teamTimeZone, { keepLocalTime: true })
                          setStartDate(newDate)
                        } catch {
                          // Handle invalid date format
                        }
                      }}
                    />
                    <Title>{t("fromTime")}</Title>
                    <DurationInput
                      disabled={isAssetView || startChangeBlocked ? true : disabled}
                      value={isAssetView ? startTimeAssetView : startTime}
                      onChange={isAssetView ? setStartTimeAssetView : setStartTime}
                      required
                      isTime
                    />
                  </Container>
                </span>
                <Container>
                  <Title>{t("toDate")}</Title>
                  <Text
                    type="date"
                    disabled
                    value={endDate ? endDate.toFormat("yyyy-MM-dd") : ""}
                  />
                  <Title>{t("toTime")}</Title>
                  <DurationInput
                    disabled
                    value={durationValue}
                  />
                </Container>
              </>
            </div>
          </>
        )}

        {showTaskSections && !isDefinition && (
          <div className="action-links" onClick={() => setShowAdditionals(!showAdditionals)}>
            <div className="action-container">
              <span>{t("additionalInformation")}</span>
              {showAdditionals ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </div>
          </div>
        )}

        {showTaskSections && (showAdditionals || isDefinition) && (
          <div className="additional-info">
            {!isDefinition && (
              <Container>
                <Title>{t("state")}</Title>
                <TaskStatePicker required disabled={disabled} value={state} onChange={setState} />
              </Container>
            )}
            {!(showLims && !isDefinition && analysis) && (
              <Container>
                <Title>{t("description")}</Title>
                <Text disabled={disabled} large value={description} onChange={setDescription} rows={descriptionTextRows} />
              </Container>
            )}

            {isAnalysisSetupView ? (
              <>
                <PeopleSetup
                  setuptime={setuptime}
                  setSetuptime={setSetuptime}
                  isDraft={isDraft}
                  perSampleDuration={perSampleDuration}
                  setPerSampleDuration={setPerSampleDuration}
                  disabled={disabled}
                  competenceGroups={competenceGroups}
                  allowedGroupIds={allowedGroupIds}
                  setAllowedGroupIds={setAllowedGroupIds}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  startTime={startTime}
                  setStartTime={setStartTime}
                  teamTimeZone={teamTimeZone}
                  task={task}
                  startChangeBlocked={startChangeBlocked}
                />
                {assetRequirements.map((requirement, index) => (
                  <AssetSetup
                    key={index}
                    index={index}
                    assetRequirement={requirement}
                    setAssetRequirement={(field, value) => handleAssetRequirementChange(index, field, value)}
                    removeAssetRequirement={() => removeAssetRequirement(index)}
                    assets={assets}
                    isDefinition={isDefinition}
                    disabled={disabled}
                    sortedCollection={sortedCollection}
                  />
                ))}
              </>
            ) : (
              <>
                {assetRequirements.map((requirement, index) => (
                  <AssetSetup
                    key={index}
                    index={index}
                    assetRequirement={requirement}
                    setAssetRequirement={(field, value) => handleAssetRequirementChange(index, field, value)}
                    removeAssetRequirement={() => removeAssetRequirement(index)}
                    assets={assets}
                    isDefinition={isDefinition}
                    disabled={disabled}
                    sortedCollection={sortedCollection}
                  />
                ))}
                <PeopleSetup
                  setuptime={setuptime}
                  setSetuptime={setSetuptime}
                  isDraft={isDraft}
                  perSampleDuration={perSampleDuration}
                  setPerSampleDuration={setPerSampleDuration}
                  disabled={disabled}
                  competenceGroups={competenceGroups}
                  allowedGroupIds={allowedGroupIds}
                  setAllowedGroupIds={setAllowedGroupIds}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  startTime={startTime}
                  setStartTime={setStartTime}
                  teamTimeZone={teamTimeZone}
                  task={task}
                  startChangeBlocked={startChangeBlocked}
                />
              </>
            )}
            {isDefinition && (
              <div className="add-new-asset">
                <p>{t("newInstrument")}</p>
                <Button title={t("addInstrument")} onClick={addAssetRequirement}>
                  <FiPlus/>
                </Button>
              </div>
            )}
          </div>
        )}

        {showQcSections && showLims && !isDefinition && analysis && (
          <div className="AnalysisTaskConfig-results">
            {/* SPEC-060 (#4132): as a whole TAB this is not an accordion — the tab's title already
                says what the tab is, so the collapsible's header is dropped entirely here. In "all"
                mode this is the collapsible section it has always been.

                #4155 removed the SST-ready badge from BOTH shapes. In the tab it had no header to
                sit in and ended up floating on a row of its own above Consumables, which is the
                odd placement the issue reports; in the accordion header it was a duplicate nobody
                could reach from the tab. The SST itself is unaffected: the panel below states the
                SST's status where the work is done, and the send-to-review HARD GATE in
                ResultEntryPanel still blocks until the SST has passed and been signed. */}
            {!qcAlwaysOpen && (
              <div className="action-links" onClick={() => setShowResults(!showResults)}>
                <div className="action-container">
                  {/* SPEC-022 (K18): renamed from "QC result entry" — the section now spans the
                      whole QC workflow (entry → review → QA decisions). */}
                  <span>{t("limsQcResultsApprovals")}</span>
                  {showResults ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </div>
              </div>
            )}
            {(qcAlwaysOpen || showResults) && (
              <>
                {/* SPEC-060 (#4132): the consumables block lives HERE, with result entry,
                    because that is where the data belongs — the selection is persisted on the
                    QC RESULT (`save_result` sets reagent_lot_ids), never on the task, whose
                    submit payload does not carry it. Keeping the picker and the result in one
                    view is also what removes the cross-tab coupling that made #4132 more than
                    a tab change.

                    This SUPERSEDES RR8 obs. 4/5/6, which deliberately put the picker on the
                    task form so consumables could be recorded WITHOUT opening result entry.
                    That premise was that result entry was buried in a collapsed section; now
                    it is a tab of its own, so the friction RR8 avoided no longer exists.
                    Customer decision, 2026-09-08.

                    The stack is unchanged (picker -> requirements -> selected list), as is all
                    of its state and logic: `reagentLotIds`, the seeding from existing results,
                    the locked-swap guard and the requirement matching are untouched. The
                    block's own `showLims && analysis` guard is dropped as redundant — the
                    enclosing QC section already requires both. */}
                <div className="container-compartment AnalysisTaskConfig-consumables">
                  <p>{t("limsResultConsumables")}</p>
                  {/* The picker offers active consumables of EVERY type (reagent / standard /
                      column / glassware). The camera button is a NON-FUNCTIONAL placeholder
                      signalling that barcode/QR scan intake of consumables is coming (no handler
                      yet); kept disabled so it reads clearly as "coming soon". */}
                  <div className="AnalysisTaskConfig-consumable-picker">
                    <div className="AnalysisTaskConfig-consumable-select">
                      <Select
                        isMulti
                        collection={reagentCollection}
                        value={reagentLotIds}
                        onChange={handleConsumablesChange}
                        menuPlacement="bottom"
                      />
                    </div>
                    <button
                      type="button"
                      className="AnalysisTaskConfig-consumable-scan"
                      disabled
                      aria-disabled="true"
                      title={t("limsConsumablesScanComingSoon")}
                      aria-label={t("limsConsumablesScanComingSoon")}
                    >
                      <FiCamera />
                    </button>
                  </div>
                  {/* F8 (SPEC-040): the analysis's consumable REQUIREMENTS + per-requirement coverage.
                      Only the qualifying consumables are selectable (the picker is hard-filtered); this
                      shows WHICH requirements still need a consumable (fail-fast — the server is the
                      authoritative completeness gate at send-to-review). */}
                  {requiredConsumables.length > 0 && (
                    <div className="AnalysisTaskConfig-requirements">
                      <div className="AnalysisTaskConfig-requirements-title">{t("limsRequiredConsumables")}</div>
                      <ul className="AnalysisTaskConfig-requirements-list">
                        {requirementCoverage.map(({ requirement, covered }, i) => (
                          <li key={i} className="AnalysisTaskConfig-requirement-item">
                            <span className="AnalysisTaskConfig-requirement-label">
                              {requirementLabel(requirement, t)}
                            </span>
                            <span
                              className={
                                "AnalysisTaskConfig-requirement-status " +
                                (covered ? "is-covered" : "is-missing")
                              }
                            >
                              {covered ? t("limsRequiredConsumableCovered") : t("limsRequiredConsumableMissing")}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {uncoveredRequirements.length > 0 && (
                        <div className="AnalysisTaskConfig-requirements-hint">
                          <FiAlertTriangle />{" "}
                          {t("limsRequiredConsumablesIncomplete", { remaining: uncoveredRequirements.length })}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Per attached consumable, a VISIBLE Active / Expired / Overdue status badge
                      (PRD-003 Area 3) — so a used-up column or overdue flask is obvious, not only
                      surfaced as a soft warning. The selection is never hard-blocked. */}
                  {selectedConsumables.length > 0 && (
                    <ul className="AnalysisTaskConfig-consumable-list">
                      {selectedConsumables.map(({ consumable, badge }) => (
                        <li key={consumable.id} className="AnalysisTaskConfig-consumable-item">
                          <span className="AnalysisTaskConfig-consumable-name">
                            {consumableLabel(consumable, t)}
                          </span>
                          <span className={"AnalysisTaskConfig-consumable-status " + badge.className}>
                            {t(badge.labelKey)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {invalidSelectedConsumables.length > 0 && (
                    <div className="AnalysisTaskConfig-reagent-warning">
                      <FiAlertTriangle />{" "}
                      {t("limsResultReagentExpiredWarning", {
                        lots: invalidSelectedConsumables.map((s) => consumableLabel(s.consumable, t)).join(", "),
                      })}
                    </div>
                  )}
                  {/* Changing/removing a consumable already recorded on an in-review/reviewed
                      result requires a stated reason -> audit (PRD-003 Area 3). Reuses the
                      e-signature dialog in reason-only mode (no capability gate — lab inventory).
                      On cancel the deferred selection is discarded (the picker keeps its value). */}
                  {pendingSwap && (
                    <ESignatureModal
                      title={t("limsConsumableSwapTitle")}
                      onSign={handleSwapSign}
                      onCancel={() => setPendingSwap(null)}
                    />
                  )}
                </div>
                {(effectiveForskrift || hasLinkedForskrift) && (
                  <ForskriftViewer effective={effectiveForskrift} />
                )}
                {requiresSst && (
                  <SstPanel analysisId={task.analysisId} analysisDefinitionId={analysisDefinitionId} />
                )}
                <ResultEntryPanel
                  analysis={analysis}
                  analysisDefinitionId={analysisDefinitionId}
                  primaryAssetId={primaryAsset}
                  reagentLotIds={reagentLotIds}
                />
                <Container>
                  <Title>{t("limsResultRunNotes")}</Title>
                  <Text disabled={disabled} large value={description} onChange={setDescription} rows={descriptionTextRows} />
                </Container>
              </>
            )}
          </div>
        )}
      </div>
    </ConfigContainer>
  )
}

// A type-tagged consumable option/label, e.g. "HPLC C18 — SN-4471 (Column)" or
// "Sodium hydroxide 0.1 M — LOT-42 (Reagent)". The secondary identifier is the lot for
// reagents/standards and the serial for columns/glassware; the trailing tag is the type label.
function consumableLabel(consumable, t) {
  if (!consumable) return ""
  const type = consumableType(consumable)
  const secondary = consumable.lotNumber || consumable.serial || ""
  const base = secondary ? `${consumable.name} — ${secondary}` : consumable.name
  const typeLabel = t(CONSUMABLE_TYPE_FIELDS[type].labelKey)
  return `${base} (${typeLabel})`
}

// F8 (SPEC-040) — the advisory quantity of a requirement as text ("10–50 mg" / "≥ 10 mg" /
// "≤ 50 mg" / "" when unspecified). Returns "" when no bound and no unit are set.
function requirementQuantityText(quantity) {
  const q = quantity
  if (!q) return ""
  let range = ""
  if (q.min != null && q.max != null) range = `${q.min}–${q.max}`
  else if (q.min != null) range = `≥ ${q.min}`
  else if (q.max != null) range = `≤ ${q.max}`
  const unit = q.unit && q.unit.trim() !== "" ? q.unit : ""
  return [range, unit].filter(Boolean).join(" ")
}

// F8 (SPEC-040) — a human label for a consumable requirement, e.g. "Column: HPLC C18" or
// "Standard (primary): Paracetamol RS · 10–50 mg". Type label first, then the free-text name, an
// optional reference-standard class, and the advisory quantity range when present.
function requirementLabel(requirement, t) {
  if (!requirement) return ""
  const typeKey = CONSUMABLE_TYPE_FIELDS[requirement.type] && CONSUMABLE_TYPE_FIELDS[requirement.type].labelKey
  let head = typeKey ? t(typeKey) : requirement.type || ""
  if (requirement.standardClass) head += ` (${t(standardClassLabelKey(requirement.standardClass))})`
  const parts = [requirement.name ? `${head}: ${requirement.name}` : head]
  const qty = requirementQuantityText(requirement.quantity)
  if (qty) parts.push(qty)
  return parts.join(" · ")
}

// Badge colour classes for a selected-consumable status pill (matched in index.scss).
const BADGE_OK = "is-active"
const BADGE_WARN = "is-quarantine"
const BADGE_INVALID = "is-invalid"

// Per-item status badge for a selected consumable on the analysis run. For a REFERENCE STANDARD
// the richer standardStatus() drives the badge (approved / quarantine / expiring-soon / expired /
// discarded, SPEC-015); for the other types the simple validityStatus() (active / expired /
// overdue). `invalid` marks the soft-warning set (expired/overdue/discarded — NOT quarantine /
// expiring-soon, which are warnings, never blocks). Returns { labelKey, className, invalid }.
function consumableBadge(consumable) {
  if (consumableType(consumable) === "standard") {
    const status = standardStatus(consumable)
    if (status === "expired" || status === "discarded") {
      return {
        labelKey: status === "expired" ? "limsStandardStatusExpired" : "limsStandardStatusDiscarded",
        className: BADGE_INVALID,
        invalid: true,
      }
    }
    if (status === "quarantine") {
      return { labelKey: "limsStandardStatusQuarantine", className: BADGE_WARN, invalid: false }
    }
    if (isStandardExpiringSoon(consumable)) {
      return { labelKey: "limsStandardExpiringSoon", className: BADGE_WARN, invalid: false }
    }
    return { labelKey: "limsStandardStatusApproved", className: BADGE_OK, invalid: false }
  }
  const status = validityStatus(consumable)
  if (status === "expired") return { labelKey: "limsReagentExpired", className: BADGE_INVALID, invalid: true }
  if (status === "overdue") return { labelKey: "limsConsumableOverdue", className: BADGE_INVALID, invalid: true }
  return { labelKey: "limsReagentActive", className: BADGE_OK, invalid: false }
}

// F8 (SPEC-040) — whether a consumable satisfies ONE requirement (mirrors the BE
// `_consumable_matches_requirement`): it must be ACTIVE (not expired/overdue/used-up), its type
// must match the requirement's `type` (a requirement without a type matches any type), and when the
// requirement pins a `standardClass` the consumable's standardClass must equal it. Quantity is
// advisory and is intentionally NOT matched here.
function matchesRequirement(consumable, requirement) {
  if (!consumable) return false
  if (validityStatus(consumable) !== "active") return false
  if (requirement.type && consumableType(consumable) !== requirement.type) return false
  if (requirement.standardClass && (consumable.standardClass || null) !== requirement.standardClass) return false
  return true
}

// Whether a consumable may be OFFERED for this run: with NO requirements, any active consumable
// qualifies; with requirements, it must satisfy at least one. (An inactive consumable never
// qualifies — the picker only ever offers active items; historical selections are kept separately.)
function matchesRequirements(consumable, requirements) {
  if (!consumable) return false
  if (!requirements || requirements.length === 0) return validityStatus(consumable) === "active"
  return requirements.some((req) => matchesRequirement(consumable, req))
}

// F8 (SPEC-040) — the REASON a manually-entered / scanned consumable id does not qualify, as an
// i18n key (or null when it DOES qualify). Hard filtering covers the dropdown, but a scanned/typed
// id bypasses it; this turns a silent rejection into an explained one (USER decision 9a). Order:
// not-found → inactive (expired/overdue/used-up) → no requirement matched (wrong type / wrong
// standard class / nothing required matches). Returns one of the limsRequiredConsumableReject* keys.
function rejectionReason(consumable, requirements) {
  if (!consumable) return "limsRequiredConsumableRejectNotFound"
  const status = validityStatus(consumable)
  if (status === "expired") return "limsRequiredConsumableRejectExpired"
  if (status === "overdue") return "limsRequiredConsumableRejectOverdue"
  if (status !== "active") return "limsRequiredConsumableRejectInactive"
  if (requirements && requirements.length > 0 && !requirements.some((req) => matchesRequirement(consumable, req))) {
    return "limsRequiredConsumableRejectNoMatch"
  }
  return null
}
